<template>
  <div class="autocomplete-field" onkeydown="return event.key != 'Enter'">
    <b-form-group id="input-group-suburb">
      <b-img
        v-if="prependIcon"
        class="prepend-icon"
        :src="require('@/assets/icons/pin-location.svg')"
      ></b-img>

      <b-form-input
        id="place-autocomplete"
        name="place-autocomplete"
        v-model="query"
        type="text"
        required
        :placeholder="placeholder"
        :state="inputState"
        :class="inputClass"
        :style="prependIcon ? 'padding-left: 36px' : ''"
        aria-describedby="input-suburb-feedback"
        @input="$emit('input', query)"
        @keyup="onKeyupLocation($event)"
        @keydown.esc="selectLocation(-1)"
      ></b-form-input>

      <b-form-invalid-feedback id="input-suburb-feedback">
        <!-- eslint-disable-next-line -->
        {{ inputError }}
      </b-form-invalid-feedback>
    </b-form-group>

    <ul class="autocomplete-list" v-if="predictions && showPredictions">
      <li
        class="autocomplete-list-item"
        v-for="(item, index) in predictions"
        :key="item.id"
        :item="item"
        :class="{ selected: curIndex === index }"
        @click="selectLocation(index)"
        @keydown.esc="selectLocation(-1)"
      >
        <span class="autocomplete-list-item-icon"></span>
        <span class="autocomplete-list-item-label">{{ item.description }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
import geocode from './Geocode'

const KEYCODE = {
  ESC: 27,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  ENTER: 13,
  SPACE: 32,
  TAB: 9,
}

const API_REQUEST_OPTIONS = [
  'bounds',
  'location',
  'component-restrictions',
  'offset',
  'radius',
  'types',
]

export default {
  props: {
    value: String,

    placeholder: String,

    inputState: Boolean,

    inputError: String,

    inputClass: String,

    apiKey: String,

    prependIcon: Boolean,

    baseUri: {
      type: String,
      default: 'https://maps.googleapis.com/maps/api/js',
    },

    componentRestrictions: {
      type: [Boolean, Object, String],
      default: false,
    },

    custom: Boolean,

    libraries: {
      type: Array,
      default() {
        return ['geometry', 'places']
      },
    },

    bounds: {
      type: [Boolean, Object, String],
      default: false,
    },

    location: {
      type: [Boolean, Object, String],
      default: false,
    },

    offset: {
      type: Boolean,
      default: false,
    },

    radius: {
      type: Boolean,
      default: false,
    },

    types: {
      type: [Boolean, Array],
      default: false,
    },
  },

  data() {
    return {
      loaded: false,
      predictions: false,
      showPredictions: false,
      curIndex: -1,
      query: '',
      place_id: '',
      coordinates: {
        lat: '',
        lng: '',
      },
    }
  },

  methods: {
    onKeyupLocation(e) {
      this.query = e.target.value
      if (!this.query) {
        this.showPredictions = false
        return
      }

      // traverse prediction list
      if (e.keyCode === KEYCODE.UP && this.curIndex > -1) {
        this.curIndex--
      } else if (
        e.keyCode === KEYCODE.DOWN &&
        this.curIndex < this.predictions.length - 1
      ) {
        this.curIndex++
      } else if (e.keyCode === KEYCODE.ENTER) {
        this.selectLocation(this.curIndex)
      } else {
        // show predictions
        this.showPredictions = true
        this.search().then(
          (response) => {
            this.predictions = response
            this.showPredictions = true
          },
          (error) => {
            if (error) {
              this.predictions = false
            }
          },
        )
      }
    },

    selectLocation(index) {
      this.select(this.predictions[index])
      this.predictions = false
    },

    getRequestOptions() {
      const options = {
        input: this.query,
      }
      for (let i in API_REQUEST_OPTIONS) {
        if (this[i] !== undefined || this[i] !== null) {
          options[i] = this[i]
        }
      }
      return options
    },

    select(place) {
      geocode({ placeId: place.place_id }).then((response) => {
        this.query = response[0].formatted_address
        this.place_id = response[0].place_id
        this.coordinates.lat = response[0].geometry.location.lat()
        this.coordinates.lng = response[0].geometry.location.lng()

        this.$emit('input', this.query)
        this.$emit('select', this.coordinates)
        this.$emit('changed', {
          places_id: this.place_id,
          places_address: this.query,
          coordinates: this.coordinates,
        })
      })
    },

    search() {
      return new Promise((resolve, reject) => {
        if (!this.query) {
          this.predictions = false
          this.showPredictions = false
          // reject(new Error('Input empty'));
        } else {
          this.$service.getPlacePredictions(
            this.getRequestOptions(),
            (response, status) => {
              switch (status) {
                case window.google.maps.places.PlacesServiceStatus.OK:
                  resolve(response)
                  break
                default:
                  reject(new Error(`Error with status: ${status}`))
              }
            },
          )
        }
      })
    },

    removeScript() {
      let keywords = ['maps.googleapis']

      // Remove google from BOM (window object)
      window.google = undefined

      // Remove google map scripts from DOM
      let scripts = document.head.getElementsByTagName('script')

      for (let i = scripts.length - 1; i >= 0; i--) {
        let scriptSource = scripts[i].getAttribute('src')

        if (scriptSource != null) {
          if (keywords.filter((item) => scriptSource.includes(item)).length) {
            scripts[i].remove()
          }
        }
      }
    },

    loadPlacesScript() {
      this.removeScript()

      return new Promise((resolve) => {
        let script = document.createElement('script')

        script.setAttribute(
          'src',
          `${this.baseUri}?key=${this.apiKey}&libraries=${this.libraries.join(
            ',',
          )}`,
        )
        script.async = true

        document.head.appendChild(script)

        script.onload = () => {
          this.loaded = true
          resolve()
        }
      })
    },
  },

  mounted() {
    if (this.apiKey && !this.loaded) {
      this.loadPlacesScript().then(() => {
        this.$geocoder = new window.google.maps.Geocoder()
        this.$service = new window.google.maps.places.AutocompleteService()
        this.loaded = true
        this.$emit('loaded')
      })
    }
  },

  watch: {
    value(value) {
      this.query = value

      // reset location filter
      if (!this.query) {
        this.$emit('select', null)
      }
    },
  },

  /*
    {
        // An array of types specifies an explicit type or a type collection, as listed in the supported types below. If nothing is specified, all types are returned. In general only a single type is allowed. The exception is that you can safely mix the geocode and establishment types, but note that this will have the same effect as specifying no types. The supported types are: geocode instructs the Places service to return only geocoding results, rather than business results. address instructs the Places service to return only geocoding results with a precise address. establishment instructs the Places service to return only business results. the (regions) type collection instructs the Places service to return any result matching the following types: locality sublocality postal_code country administrative_area1 administrative_area2 the (cities) type collection instructs the Places service to return results that match either locality or administrative_area3.
        // Possible values: geocode, address, establishment, cities, locality, sublocality, postal_code, country, administrative_area1, administrative_area2
        type: undefined,

        // is a google.maps.LatLngBounds|google.maps.LatLngBoundsLiteral object specifying the area in which to search for places. The results are biased towards, but not restricted to, places contained within these bounds.
        bounds: undefined,

        // is a boolean specifying whether the API must return only those places that are strictly within the region defined by the given bounds. The API does not return results outside this region even if they match the user input.
        strictBounds: true|false,

        // can be used to restrict results to specific groups. Currently, you can use componentRestrictions to filter by up to 5 countries. Countries must be passed as as a two-character, ISO 3166-1 Alpha-2 compatible country code. Multiple countries must be passed as a list of country codes. z
        componentRestrictions: undefined,

        // can be used to instruct the Autocomplete widget to retrieve only Place IDs. On calling getPlace() on the Autocomplete object, the PlaceResult made available will only have the place id, types and name properties set. You can use the returned place ID with calls to the Places, Geocoding, Directions or Distance Matrix services.
        placeIdOnly: undefined,

        // is a google.maps.LatLng for prediction biasing. Predictions will be biased towards the given location and radius. Alternatively, bounds can be used.
        location: undefined,

        // is a number to determine the character position in the input term at which the service uses text for predictions (the position of the cursor in the input field).
        offset: undefined,

        // is a number to the radius of the area used for prediction biasing. The radius is specified in meters, and must always be accompanied by a location property. Alternatively, bounds can be used.
        radius: undefined
    }
    */
}
</script>

<style lang="scss" scoped>
.prepend-icon {
  position: absolute;
  top: 32px;
  left: unset;
  margin-left: 8px;
}
.search-input {
  color: black;
  border-color: white;
  height: 72px;

  &:hover,
  &:focus {
    border-color: white;
  }

  &:focus::placeholder {
    opacity: 0.3;
  }

  &::placeholder {
    color: black;
  }
}
.autocomplete-list-wrapper {
  position: absolute;
  z-index: 10;
  width: 100%;
  top: 100%;
  background: white;
}
.autocomplete-list {
  margin: 0;
  padding: 0;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);
  list-style-type: none;
  border-radius: 6px;
  z-index: 99;
  position: absolute;
  background: #fff;
  overflow: hidden;
  text-align: left;
  margin-top: -1rem;
}
.autocomplete-list-item {
  margin: 0;
  padding: 5px 15px;
  font-size: 0.8rem;
  list-style: none;
}
.autocomplete-list-item.selected,
.autocomplete-list-item:hover {
  cursor: pointer;
  background-color: #f8f9fa;
}
</style>
